import React from "react";
import PropTypes from "prop-types";
import PayPalManager from "lib/PayPalManager";
import { withStyles } from "@theconversation/ui";

// The reason this is not just "display: none;" is because the internals
// of the PayPal button will throw an error if it is rendered into an element
// that is not "visible". So instead we just collapse the element and
// hide the overflow.
const HIDDEN_STYLES = {
  overflow: "hidden",
  height: 0,
};

const DISABLED_STYLES = {
  opacity: 0.2,
};

const styles = theme => ({ error: { color: theme.palette.error.border } });

class PayPalButton extends React.Component {
  constructor(props) {
    super(props);

    this.handleInit = this.handleInit.bind(this);
    this.renderButton = this.renderButton.bind(this);
  }

  componentDidUpdate(prevProps) {
    // Ensure we don't attempt to enable/disable the actions once the
    // transaction is completed, lest PayPal raise an error
    // see https://app.bugsnag.com/the-conversation/frontend-tc-donations/errors/6050c8b49d491c0008ebdebe
    if (this.props.payPalManager.completed) {
      return;
    }
    if (this.actions) {
      const wasDisabled = prevProps.disabled || prevProps.preventDefault;
      const disabled = this.props.disabled || this.props.preventDefault;

      if (wasDisabled && !disabled) {
        this.actions.enable();
      } else if (!wasDisabled && disabled) {
        this.actions.disable();
      }
    }
    // Assuming PayPal is loaded, render the PayPal button if:
    //  - this is the first time the the donor has chosen PayPal as payment method
    //  - if we've already rendered the button and the donor changes the frequency
    //  (whether or not PayPal is currently selected)
    const needsToRender = this.props.paymentProvider === "paypal" && !this.buttons;
    const needsToChange = this.buttons && (prevProps.frequency !== this.props.frequency);
    if (this.props.payPalManager.paypal && (needsToRender || needsToChange)) {
      this.renderButton();
    }
  }

  handleInit(data, actions) {
    this.actions = actions;

    if (this.props.disabled || this.props.preventDefault) {
      this.actions.disable();
    }
  }

  /**
   * Renders a PayPal button into `div#paypal_button` using the standard
   * Buttons API
   * @see https://developer.paypal.com/docs/checkout/reference/button-interface/
   */
  renderButton() {
    const { completed, callbacks, onCancel, onClick } = this.props.payPalManager;
    // Ensure we don't attempt to unload the buttons once the transaction is
    // completed, lest PayPal raise an error
    // see https://app.bugsnag.com/the-conversation/frontend-tc-donations/errors/6050c8b33fa87f000758c5f7
    // and https://app.bugsnag.com/the-conversation/frontend-tc-donations/errors/604f79b651c1b200089ec0b5
    if (completed) {
      return;
    }
    if (this.buttons) {
      this.buttons.close();
    }
    this.buttons = this.props.payPalManager.paypal.Buttons({
      ...callbacks,
      commit: true,
      env: this.props.payPalManager.env,
      onCancel,
      onClick,
      onInit: this.handleInit,
      style: { layout: "horizontal", size: "responsive", locale: this.props.locale },
    });
    this.buttons.render("#paypal_button");
  }

  render() {
    const errorSpan = this.props.error ? <span className={this.props.classes.error}>{this.props.error}</span> : "";
    const visibleStyle = this.props.paymentProvider === "paypal" ? {} : HIDDEN_STYLES;
    const disabledStyle = this.props.disabled ? DISABLED_STYLES : {};
    const activeClass = this.props.disabled ? "disabled" : "enabled";

    return (
      <div
        id="paypal"
        className={activeClass}
        style={{ ...visibleStyle, ...disabledStyle }}
      >
        <div id="paypal_button" />
        {errorSpan}
      </div>
    );
  }
}

PayPalButton.defaultProps = {
  classes: { error: "" },
  disabled: false,
  error: "",
  frequency: "once",
  locale: "",
  preventDefault: false,
  paymentProvider: "",
  payPalManager: new PayPalManager({ }),
};

PayPalButton.propTypes = {
  classes: PropTypes.shape({ error: PropTypes.string }),
  disabled: PropTypes.bool,
  error: PropTypes.node,
  frequency: PropTypes.oneOf(["once", "monthly"]),
  locale: PropTypes.string,
  preventDefault: PropTypes.bool,
  paymentProvider: PropTypes.string,
  payPalManager: PropTypes.instanceOf(PayPalManager),
};

export default withStyles(styles)(PayPalButton);
